import React from "react";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormControl, FormDescription, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  useGetVoicesQuery,
  useGetLlmModelAvailabilityQuery,
  useRefreshVoiceModelsMutation,
} from "@/store/admin_api";
import VoicePreviewButton from "@/components/ui/voice-preview-button";
import {
  AI_LLM_PROVIDERS,
  AI_STT_PROVIDERS,
  AI_VOICE_ORCHESTRATORS,
  AI_VOICE_PROVIDERS,
  REALTIME_VOICES_OPENAI,
  getLlmModelsForProvider,
  getSttModelsForProvider,
  isLlmModelDeprecated,
} from "@/model/interface/AiAgent";
import CollapsibleCard from "./CollapsibleCard";
import { TemplateFormValues } from "../templateForm";

const UNSET = "__default__";

export default function TemplateVoiceEngineSection({ collapseAdvanced = false }: { collapseAdvanced?: boolean }) {
  const form = useFormContext<TemplateFormValues>();
  const { data: voices } = useGetVoicesQuery();
  const { data: llmAvailability } = useGetLlmModelAvailabilityQuery();
  const [refreshVoiceModels, { isLoading: isRefreshingVoices }] = useRefreshVoiceModelsMutation();

  const orchestrator = form.watch("orchestrator");
  const llmProvider = form.watch("llmProvider");
  const llmModels = getLlmModelsForProvider(llmProvider);
  const sttProviderValue = form.watch("sttProvider")?.trim() || "";
  const selectedVoiceProvider = form.watch("voiceProvider") || "ELEVENLABS";

  const handleRefreshVoices = async () => {
    try {
      await refreshVoiceModels().unwrap();
      toast.success("Voice list refreshed from providers");
    } catch {
      toast.error("Failed to refresh voices");
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Engine and Voice</CardTitle>
          <CardDescription>Orchestrator, language model and voice</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField control={form.control} name="orchestrator" render={({ field }) => (
              <FormItem>
                <FormLabel>Orchestrator</FormLabel>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value);
                    if (value !== "NATIVE") {
                      form.setValue("llmModel", "");
                    }
                  }}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {AI_VOICE_ORCHESTRATORS.map((orchestratorOption) => (
                      <SelectItem key={orchestratorOption.value} value={orchestratorOption.value}>
                        {orchestratorOption.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />

            <FormField control={form.control} name="llmProvider" render={({ field }) => (
              <FormItem>
                <FormLabel>LLM Provider</FormLabel>
                <Select
                  value={field.value || ""}
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue("llmModel", "");
                  }}
                >
                  <FormControl><SelectTrigger><SelectValue placeholder="Auto (default)" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {AI_LLM_PROVIDERS.map((provider) => (
                      <SelectItem key={provider.value} value={provider.value}>{provider.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormItem>
            )} />
          </div>

          {orchestrator === "NATIVE" && llmModels.length > 0 && (
            <FormField control={form.control} name="llmModel" render={({ field }) => (
              <FormItem className="max-w-sm">
                <FormLabel>LLM Model</FormLabel>
                <Select
                  value={field.value?.trim() || UNSET}
                  onValueChange={(value) => field.onChange(value === UNSET ? "" : value)}
                >
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value={UNSET}>Platform default</SelectItem>
                    {llmModels.map((model) => {
                      const deprecated = isLlmModelDeprecated(llmProvider, model.value, llmAvailability);
                      return (
                        <SelectItem key={model.value} value={model.value}>
                          <div className="flex flex-col">
                            <span>
                              {model.label}
                              {deprecated && <span className="ml-2 text-xs text-warning">no longer offered by provider</span>}
                            </span>
                            {model.description && <span className="text-xs text-muted-foreground">{model.description}</span>}
                          </div>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                <FormDescription>Overrides the platform default for all agents using this template.</FormDescription>
              </FormItem>
            )} />
          )}

          {orchestrator === "NATIVE" && (
            <>
              <FormField control={form.control} name="sttProvider" render={({ field }) => (
                <FormItem className="max-w-sm">
                  <FormLabel>Speech-to-Text Provider</FormLabel>
                  <Select
                    value={field.value?.trim() || UNSET}
                    onValueChange={(value) => {
                      field.onChange(value === UNSET ? "" : value);
                      form.setValue("sttModel", "");
                    }}
                  >
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value={UNSET}>Platform default (Deepgram)</SelectItem>
                      {AI_STT_PROVIDERS.map((provider) => (
                        <SelectItem key={provider.value} value={provider.value}>{provider.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>Which vendor transcribes the caller for agents on this template. Changing it resets the model below.</FormDescription>
                </FormItem>
              )} />

              <FormField control={form.control} name="sttModel" render={({ field }) => (
                <FormItem className="max-w-sm">
                  <FormLabel>Speech-to-Text Model</FormLabel>
                  <Select
                    value={field.value?.trim() || UNSET}
                    onValueChange={(value) => field.onChange(value === UNSET ? "" : value)}
                  >
                    <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                    <SelectContent>
                      <SelectItem value={UNSET}>Provider default</SelectItem>
                      {getSttModelsForProvider(sttProviderValue || "DEEPGRAM").map((model) => (
                        <SelectItem key={model.value} value={model.value}>
                          <div className="flex flex-col">
                            <span>{model.label}</span>
                            <span className="text-xs text-muted-foreground">{model.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription>STT model within the chosen provider, for all agents using this template.</FormDescription>
                </FormItem>
              )} />
            </>
          )}

          {orchestrator === "OPENAI_REALTIME" && (
            <FormField control={form.control} name="realtimeVoice" render={({ field }) => (
              <FormItem className="max-w-sm">
                <FormLabel>Realtime Voice (OpenAI)</FormLabel>
                <Select
                  value={field.value?.trim() || UNSET}
                  onValueChange={(value) => field.onChange(value === UNSET ? "" : value)}
                >
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value={UNSET}>Platform default</SelectItem>
                    {REALTIME_VOICES_OPENAI.map((voice) => (
                      <SelectItem key={voice.value} value={voice.value}>
                        <div className="flex flex-col">
                          <span>{voice.label}</span>
                          <span className="text-xs text-muted-foreground">{voice.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormDescription>The OpenAI voice for agents using this template. marin and cedar are the newest, most natural.</FormDescription>
              </FormItem>
            )} />
          )}

          <FormField control={form.control} name="voiceProvider" render={({ field }) => (
            <FormItem>
              <FormLabel>Voice Provider</FormLabel>
              <Select
                value={field.value || "ELEVENLABS"}
                onValueChange={(value) => {
                  field.onChange(value);
                  form.setValue("voiceId", "");
                }}
              >
                <FormControl>
                  <SelectTrigger><SelectValue placeholder="Select a provider" /></SelectTrigger>
                </FormControl>
                <SelectContent>
                  {AI_VOICE_PROVIDERS.map((provider) => (
                    <SelectItem key={provider.value} value={provider.value}>{provider.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormDescription>The TTS provider whose voices this template uses. Changing it clears the selected voice.</FormDescription>
            </FormItem>
          )} />

          <FormField control={form.control} name="voiceId" render={({ field }) => {
            const selectedVoice = voices?.find((voice) => voice.id === field.value);
            return (
              <FormItem>
                <div className="flex items-center justify-between">
                  <FormLabel>Default Voice</FormLabel>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground"
                    onClick={handleRefreshVoices}
                    disabled={isRefreshingVoices}
                  >
                    {isRefreshingVoices ? <Loader2 className="h-3 w-3 animate-spin" /> : "Refresh voices"}
                  </Button>
                </div>
                <div className="flex gap-2">
                  <Select value={field.value || ""} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="flex-1 [&_.voice-extra]:hidden">
                        <SelectValue placeholder="Select a voice" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent position="popper" sideOffset={4} className="max-h-[300px]">
                      {voices
                        ?.filter((voice) => (voice.provider || "ELEVENLABS") === selectedVoiceProvider)
                        .map((voice) => (
                          <SelectItem key={voice.id} value={voice.id} className="pr-2">
                            <span className="flex items-center justify-between gap-2 w-full">
                              <span className="flex flex-col min-w-0">
                                <span className="truncate">
                                  {voice.name}
                                  {voice.gender ? ` · ${voice.gender}` : ""}
                                  {voice.accent ? ` · ${voice.accent}` : ""}
                                </span>
                                {voice.description ? (
                                  <span className="voice-extra text-xs text-muted-foreground truncate">{voice.description}</span>
                                ) : null}
                              </span>
                              <VoicePreviewButton previewUrl={voice.previewUrl} label={voice.name} className="voice-extra h-6 w-6 shrink-0" />
                            </span>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <VoicePreviewButton previewUrl={selectedVoice?.previewUrl} label={selectedVoice?.name} />
                </div>
              </FormItem>
            );
          }} />
        </CardContent>
      </Card>

      <CollapsibleCard
        title="Default Voice Quality"
        description="Template-level ElevenLabs voice quality defaults. Agents using this template will inherit these unless overridden per-agent in Deployed Agents."
        collapsed={collapseAdvanced}
        contentClassName="space-y-6"
      >
        <FormField control={form.control} name="defaultVoiceStability" render={({ field }) => {
          const sliderValue = field.value ?? 0.5;
          return (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>Stability</FormLabel>
                <span className="text-sm font-mono text-muted-foreground">
                  {field.value !== undefined ? field.value.toFixed(2) : "Platform default 0.50"}
                </span>
              </div>
              <FormControl>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={sliderValue}
                  onChange={(event) => field.onChange(parseFloat(event.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted accent-primary"
                />
              </FormControl>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Expressive</span>
                <span>Stable</span>
              </div>
              <FormDescription>
                Lower means more expressive delivery. Recommended: 0.30 to 0.45 for sales agents.
              </FormDescription>
            </FormItem>
          );
        }} />

        <FormField control={form.control} name="defaultVoiceSimilarityBoost" render={({ field }) => {
          const sliderValue = field.value ?? 0.75;
          return (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>Similarity Boost</FormLabel>
                <span className="text-sm font-mono text-muted-foreground">
                  {field.value !== undefined ? field.value.toFixed(2) : "Platform default 0.75"}
                </span>
              </div>
              <FormControl>
                <input
                  type="range"
                  min={0}
                  max={1}
                  step={0.05}
                  value={sliderValue}
                  onChange={(event) => field.onChange(parseFloat(event.target.value))}
                  className="w-full h-2 rounded-lg appearance-none cursor-pointer bg-muted accent-primary"
                />
              </FormControl>
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>More variation</span>
                <span>Stays true to voice</span>
              </div>
              <FormDescription>
                How closely the output matches the original voice character. Recommended: 0.75 to 0.85.
              </FormDescription>
            </FormItem>
          );
        }} />

        {(form.watch("defaultVoiceStability") !== undefined || form.watch("defaultVoiceSimilarityBoost") !== undefined) && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline hover:text-foreground"
            onClick={() => {
              form.setValue("defaultVoiceStability", undefined, { shouldDirty: true });
              form.setValue("defaultVoiceSimilarityBoost", undefined, { shouldDirty: true });
            }}
          >
            Clear overrides (use platform defaults)
          </button>
        )}
      </CollapsibleCard>
    </>
  );
}
